/**
 * The selector attributes Sentry's `_htmlElementAsString` appends to every DOM breadcrumb it records
 * (`@sentry/core` emits `aria-label`, `type`, `name`, `title`, `alt` as `[attr="value"]`). A clinical
 * UI labels its buttons and inputs with the thing they act on — a patient's name, a document title —
 * so those four carry PHI; `type` is a fixed HTML keyword and stays.
 *
 * The value matcher is lazy and accepts any character rather than "anything but a quote": an
 * attribute whose own value contains a `"` would otherwise fail to match at all and pass through
 * verbatim, which is exactly the case that must not leak.
 */
const LABELLED_ATTRIBUTE = /\[(?:aria-label|alt|name|title)="[\s\S]*?"\]/g;
/** Breadcrumb `data` keys that hold a URL — navigation records `from`/`to`, fetch/xhr/http `url`. */
const URL_KEYS = ['from', 'to', 'url'];
/**
 * Sentry's `beforeBreadcrumb` hook: the last point at which a breadcrumb can be changed before it
 * enters the buffer, so nothing it strips is ever held in memory or attached to a later event.
 *
 * It only has to handle the categories that are still *recorded*. The ones whose payload cannot be
 * filtered at all — `console`, whose `data.arguments` is a positional argument list with no keyed
 * structure, and `sentry.event`, which replays the previous issue's title — are switched off in the
 * `init()` options instead, so there is no second predicate here for a decision already made once.
 *
 * The breadcrumb is copied rather than mutated: the SDK hands over an object assembled from the
 * caller's own `data`, which the code that logged it may still be using.
 * @__NO_SIDE_EFFECTS__
 */
export function filterSentryBreadcrumb(breadcrumb) {
    const filtered = { ...breadcrumb };
    if (breadcrumb.message !== undefined && breadcrumb.category?.startsWith('ui.')) {
        // Only the labelled attributes go; `button#save.btn-primary` stays, because the selector is
        // what makes a click breadcrumb worth having.
        filtered.message = breadcrumb.message.replace(LABELLED_ATTRIBUTE, '');
    }
    if (breadcrumb.data) {
        filtered.data = filterBreadcrumbData(breadcrumb.data);
    }
    return filtered;
}
/**
 * Drops the query string and fragment from every URL a breadcrumb records. Path segments are
 * sanctioned — a resource id in a route is how a breadcrumb trail is read — but a query parameter or
 * fragment carries free text: a search term, a filter, a patient name typed into a lookup.
 *
 * The keys are matched on every category rather than only navigation/fetch/xhr, so a server-side
 * `http` breadcrumb (which also carries `url`) is covered without maintaining a category list that
 * would silently drift as the SDK adds integrations.
 */
function filterBreadcrumbData(data) {
    const filtered = { ...data };
    for (const key of URL_KEYS) {
        const value = filtered[key];
        if (typeof value === 'string') {
            filtered[key] = stripUrlDetails(value);
        }
    }
    return filtered;
}
function stripUrlDetails(url) {
    const boundary = url.search(/[?#]/);
    return boundary === -1 ? url : url.slice(0, boundary);
}
//# sourceMappingURL=filterSentryBreadcrumb.js.map